import type { Data, Layout } from 'plotly.js'
import { isDataArray } from './chart-helpers'

// ============================================================================
// Types
// ============================================================================

// Plotly chart document type - represents a complete Plotly chart structure.
// layout may be Partial<Layout> or a record.
export type PlotlyChart = {
  data?: Data[]
  layout?: Partial<Layout> | Record<string, unknown>
}

// ============================================================================
// Type Guards
// ============================================================================

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Shared guard for Plotly chart documents used across the app. */
export function isPlotlyChart(value: unknown): value is PlotlyChart {
  if (!isRecord(value)) return false

  const maybeChart = value as { data?: unknown; layout?: unknown }

  const hasDataArray = isDataArray(maybeChart.data)
  const hasLayoutObject = isRecord(maybeChart.layout)

  return hasDataArray || hasLayoutObject
}

// ============================================================================
// Chart document → data/layout (for rendering)
// ============================================================================

/** Extract title text from Plotly layout (layout.title.text). */
export function getTitleFromLayout(layout: PlotlyChart['layout']): string | undefined {
  if (!layout || typeof layout !== 'object' || layout === null) return undefined
  const title = (layout as Record<string, unknown>).title
  if (title && typeof title === 'object' && title !== null && 'text' in title) {
    return String((title as { text?: unknown }).text ?? '')
  }
  return undefined
}

function plotlyTraceFingerprint(trace: unknown, index: number): string {
  if (!isRecord(trace)) return `${index}:?`
  const type = typeof trace.type === 'string' ? trace.type : ''
  const name = typeof trace.name === 'string' ? trace.name.slice(0, 40) : ''
  const x = trace.x
  const xlen = Array.isArray(x) ? x.length : ''
  const y = trace.y
  const ylen = Array.isArray(y) ? y.length : ''
  return `${index}:${type}:${name}:${String(xlen)}x${String(ylen)}`
}

/**
 * Segment for React list keys: prefers Plotly `layout.uirevision` or `layout.meta` id when present;
 * otherwise a compact fingerprint from trace types, names, and series lengths so charts with the same
 * title still differ. Callers should append a list index when the same document could appear twice.
 */
export function getPlotlyChartStableKeySegment(chart: PlotlyChart): string {
  if (chart.layout && typeof chart.layout === 'object' && chart.layout !== null) {
    const layout = chart.layout as Record<string, unknown>
    const uirev = layout.uirevision
    if (typeof uirev === 'string' && uirev.length > 0) return `u:${uirev}`
    if (typeof uirev === 'number' && Number.isFinite(uirev)) return `u:${String(uirev)}`
    const meta = layout.meta
    if (typeof meta === 'string' && meta.length > 0) return `m:${meta.slice(0, 80)}`
    if (isRecord(meta) && typeof meta.id === 'string' && meta.id.length > 0) return `id:${meta.id}`
  }
  const traces = Array.isArray(chart.data) ? chart.data : []
  const parts = traces.map((trace, i) => plotlyTraceFingerprint(trace, i))
  const fp = parts.join(';')
  return fp.length > 180 ? `fp:${fp.slice(0, 180)}…` : `fp:${fp}`
}

/** Normalize chart to data array and layout object for rendering. No transformations.
 *  Clone layout so downstream consumers (Plotly, helpers) cannot mutate the source object.
 */
export function getChartDataAndLayout(chart: PlotlyChart): {
  data: Data[]
  layout: Partial<Layout>
} {
  const data = Array.isArray(chart.data) ? chart.data : []
  const layout =
    chart.layout && typeof chart.layout === 'object' && chart.layout !== null
      ? { ...(chart.layout as Partial<Layout>) }
      : {}
  return { data, layout }
}

// WebGL trace types that require WebGL; map to standard (SVG/Canvas) equivalents when WebGL is unavailable
const WEBGL_TO_STANDARD_TYPE: Record<string, string> = {
  scattergl: 'scatter',
  pointcloud: 'scatter',
  heatmapgl: 'heatmap',
  contourgl: 'contour',
}

let cachedWebGLAvailable: boolean | null = null

function isWebGLAvailable(): boolean {
  if (cachedWebGLAvailable !== null) return cachedWebGLAvailable

  // On the server (SSR) or non-browser environments, treat WebGL as unavailable.
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    cachedWebGLAvailable = false
    return cachedWebGLAvailable
  }

  try {
    const canvas = document.createElement('canvas')
    const gl =
      canvas.getContext('webgl') ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (canvas.getContext('experimental-webgl') as any | null)
    cachedWebGLAvailable = !!gl
  } catch {
    cachedWebGLAvailable = false
  }

  return cachedWebGLAvailable
}

/**
 * Unconditionally map WebGL trace types (`scattergl`, etc.) to standard SVG/Canvas
 * equivalents. This is not gated on `isWebGLAvailable()` — we always normalize so
 * large reports do not accumulate many concurrent WebGL contexts. Returns a new
 * array; does not mutate input.
 */
export function normalizeWebGLTracesToStandard(data: Data[]): Data[] {
  if (!Array.isArray(data) || data.length === 0) return data

  // Probe WebGL once (cached) so the helper still behaves correctly if other
  // callers rely on it, but always normalize WebGL traces to standard ones to
  // avoid accumulating many active WebGL contexts.
  void isWebGLAvailable()

  return data.map((trace) => {
    const t = trace as Record<string, unknown>
    const type = typeof t?.type === 'string' ? t.type.toLowerCase() : ''
    const standardType = WEBGL_TO_STANDARD_TYPE[type]
    if (standardType) {
      return { ...trace, type: standardType } as Data
    }
    return trace
  })
}

// ============================================================================
// Large-dataset optimizations (box plots)
// ============================================================================

/** Above this many points per box trace we replace raw y with summary stats to avoid DOM/crash. */
export const BOX_TRACE_POINTS_THRESHOLD = 500

/** Above this many box traces in one chart we force boxpoints off for all box traces. */
export const BOX_CHART_TRACES_THRESHOLD = 60

function sortedCopy(arr: number[]): number[] {
  const out = arr.slice()
  out.sort((a, b) => a - b)
  return out
}

function quantile(sorted: number[], p: number): number {
  if (sorted.length === 0) return NaN
  const index = p * (sorted.length - 1)
  const lo = Math.floor(index)
  const hi = Math.ceil(index)
  if (lo === hi) return sorted[lo]
  return sorted[lo] + (index - lo) * (sorted[hi] - sorted[lo])
}

/** Compute box statistics from a numeric array (for Plotly summary-format box). */
function computeBoxStats(y: number[]): {
  q1: number
  median: number
  q3: number
  lowerfence: number
  upperfence: number
  mean: number
  sd: number
  min: number
  max: number
} {
  const sorted = sortedCopy(y)
  const n = sorted.length
  const q1 = quantile(sorted, 0.25)
  const median = quantile(sorted, 0.5)
  const q3 = quantile(sorted, 0.75)
  const iqr = q3 - q1
  const lowerfence = sorted[0]
  const upperfence = sorted[n - 1]
  const mean = n === 0 ? 0 : sorted.reduce((a, b) => a + b, 0) / n
  const variance = n === 0 ? 0 : sorted.reduce((sum, v) => sum + (v - mean) ** 2, 0) / n
  const sd = Math.sqrt(variance)
  return {
    q1,
    median,
    q3,
    lowerfence,
    upperfence,
    mean,
    sd,
    min: lowerfence,
    max: upperfence,
  }
}

/**
 * Optimize box traces for large datasets: replace raw y arrays with Plotly summary format
 * (q1, median, q3, lowerfence, upperfence) when point count exceeds threshold. Also
 * forces boxpoints to false for very large traces so Plotly does not draw thousands of
 * points. Preserves visual and hover (hovertemplate still works with summary).
 * Returns new data array; does not mutate.
 */
export function optimizeBoxTraces(
  data: Data[],
  pointThreshold: number = BOX_TRACE_POINTS_THRESHOLD,
  traceCountThreshold: number = BOX_CHART_TRACES_THRESHOLD,
): Data[] {
  if (!Array.isArray(data) || data.length === 0) return data

  const boxTraceCount = data.filter((t) => (t as Record<string, unknown>)?.type === 'box').length
  const forceNoPoints = boxTraceCount > traceCountThreshold

  return data.map((trace) => {
    const t = trace as Record<string, unknown>
    if (t?.type !== 'box') return trace

    const y = t.y
    if (!Array.isArray(y)) return trace

    const numbers = y.filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    const overThreshold = numbers.length > pointThreshold
    if (!overThreshold && !forceNoPoints) return trace

    if (overThreshold) {
      const stats = computeBoxStats(numbers)
      const { q1, median, q3, lowerfence, upperfence, mean, sd, min, max } = stats
      const { y: _y, ...rest } = t
      const hoverStr =
        typeof t.hovertemplate === 'string'
          ? t.hovertemplate
          : `<b>%{x}</b><br>Count: ${numbers.length}<br>Mean: ${mean.toFixed(2)}<br>Median: ${median.toFixed(2)}<br>Std Dev: ${sd.toFixed(2)}<br>Q1: ${q1.toFixed(2)}<br>Q3: ${q3.toFixed(2)}<br>Min: ${min.toFixed(2)}<br>Max: ${max.toFixed(2)}<extra></extra>`
      // Plotly summary-format box needs x for categorical axis (one value per box)
      const xVal = Array.isArray(t.x) && t.x.length > 0 ? t.x[0] : t.name ?? ''
      return {
        ...rest,
        type: 'box',
        x: [xVal],
        q1: [q1],
        median: [median],
        q3: [q3],
        lowerfence: [lowerfence],
        upperfence: [upperfence],
        ...(t.boxmean === 'sd' || t.boxmean === true ? { mean: [mean], sd: [sd] } : {}),
        boxpoints: false,
        hovertemplate: hoverStr,
      } as Data
    }

    if (forceNoPoints) {
      return { ...trace, boxpoints: false } as Data
    }

    return trace
  })
}

// ============================================================================
// Large-dataset optimizations (scatter / probability-style charts)
// ============================================================================

/** Above this many points per scatter trace we start optimizing the render mode. */
export const SCATTER_POINTS_THRESHOLD = 400

/** Hard cap for number of points we keep per scatter trace after downsampling. */
export const SCATTER_MAX_POINTS = 700

/** Max total scatter points per chart; when exceeded, per-trace cap is reduced so chart stays under this. */
const SCATTER_CHART_TOTAL_POINTS_LIMIT = 5000

/** Estimate point count from Plotly binary bdata (base64-encoded float64, one axis = 8 bytes per point). */
function estimatePointCountFromBdata(bdata: string, bytesPerPoint: number = 8): number {
  if (typeof bdata !== 'string' || bdata.length === 0) return 0
  const decodedBytes = Math.floor((bdata.length * 3) / 4)
  return Math.floor(decodedBytes / bytesPerPoint)
}

function getScatterPointCount(t: Record<string, unknown>): number {
  const x = t.x
  const y = t.y
  if (Array.isArray(x) && x.length > 0) return x.length
  if (Array.isArray(y) && y.length > 0) return y.length
  if (
    x &&
    typeof x === 'object' &&
    'bdata' in x &&
    typeof (x as { bdata?: string }).bdata === 'string'
  ) {
    return estimatePointCountFromBdata((x as { bdata: string }).bdata)
  }
  if (
    y &&
    typeof y === 'object' &&
    'bdata' in y &&
    typeof (y as { bdata?: string }).bdata === 'string'
  ) {
    return estimatePointCountFromBdata((y as { bdata: string }).bdata)
  }
  return 0
}

function isBdataObject(value: unknown): value is { dtype?: string; bdata?: string } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const v = value as { dtype?: unknown; bdata?: unknown }
  return typeof v.bdata === 'string'
}

/** Decode Plotly bdata (base64 float64, little-endian) to number[]. Uses Float64Array for speed. */
function decodeBdataToFloat64(bdata: string): number[] {
  if (typeof atob !== 'function') return []
  const binary = atob(bdata)
  const length = binary.length
  const bytes = new Uint8Array(length)
  for (let i = 0; i < length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  const count = Math.floor(bytes.byteLength / 8)
  if (count <= 0) return []
  const f64 = new Float64Array(bytes.buffer, 0, count)
  return Array.from(f64)
}

function toNumericArray(coord: unknown): number[] | null {
  if (Array.isArray(coord)) {
    const result: number[] = []
    for (const v of coord) {
      if (typeof v === 'number' && Number.isFinite(v)) {
        result.push(v)
      }
    }
    return result
  }

  if (isBdataObject(coord)) {
    return decodeBdataToFloat64(coord.bdata ?? '')
  }

  return null
}

function buildDownsampledIndices(total: number, maxPoints: number): number[] {
  if (total <= maxPoints) {
    return Array.from({ length: total }, (_, i) => i)
  }

  const step = total / maxPoints
  const indices: number[] = []
  for (let i = 0; i < total; i += step) {
    const idx = Math.floor(i)
    if (indices.length === 0 || indices[indices.length - 1] !== idx) {
      indices.push(idx)
    }
  }
  if (indices[indices.length - 1] !== total - 1) {
    indices.push(total - 1)
  }
  return indices
}

function selectByIndices(values: number[], indices: number[]): number[] {
  const out: number[] = []
  for (const idx of indices) {
    if (idx >= 0 && idx < values.length) {
      out.push(values[idx])
    }
  }
  return out
}

/**
 * For scatter traces (including former scattergl converted to scatter), downsample
 * very large traces so we keep at most maxPoints per trace and at most
 * SCATTER_CHART_TOTAL_POINTS_LIMIT per chart. This preserves the overall curve
 * while avoiding DOM explosions that can crash the browser. Marker mode is
 * preserved so probability plots still render as dots. Traces with bdata are
 * decoded and replaced with plain arrays so Plotly never handles huge bdata.
 */
export function optimizeScatterTraces(
  data: Data[],
  maxPoints: number = SCATTER_MAX_POINTS,
): Data[] {
  if (!Array.isArray(data) || data.length === 0) return data

  const scatterTraces = data.filter((trace) => {
    const t = trace as Record<string, unknown>
    const type = typeof t?.type === 'string' ? (t.type as string).toLowerCase() : ''
    return type === 'scatter'
  })
  const scatterCount = scatterTraces.length
  if (scatterCount === 0) return data

  const totalEstimated = scatterTraces.reduce(
    (sum, trace) => sum + getScatterPointCount(trace as Record<string, unknown>),
    0,
  )
  const perTraceMax =
    totalEstimated <= SCATTER_CHART_TOTAL_POINTS_LIMIT
      ? maxPoints
      : Math.max(400, Math.floor(SCATTER_CHART_TOTAL_POINTS_LIMIT / scatterCount))

  return data.map((trace) => {
    const t = trace as Record<string, unknown>
    const rawType = t.type
    const type = typeof rawType === 'string' ? rawType.toLowerCase() : ''
    if (type !== 'scatter') return trace

    const pointCount = getScatterPointCount(t)
    if (pointCount <= perTraceMax || pointCount === 0) {
      if (pointCount > 0 && (isBdataObject(t.x) || isBdataObject(t.y))) {
        const xValues = toNumericArray(t.x)
        const yValues = toNumericArray(t.y)
        const effectiveLength = Math.max(
          xValues ? xValues.length : 0,
          yValues ? yValues.length : 0,
          pointCount,
        )
        if (effectiveLength > 0) {
          const next: Record<string, unknown> = { ...t }
          if (xValues?.length) next.x = xValues
          if (yValues?.length) next.y = yValues
          return next as Data
        }
      }
      return trace
    }

    const xValues = toNumericArray(t.x)
    const yValues = toNumericArray(t.y)

    const effectiveLength = Math.max(
      xValues ? xValues.length : 0,
      yValues ? yValues.length : 0,
      pointCount,
    )
    if (effectiveLength === 0) return trace

    const indices = buildDownsampledIndices(effectiveLength, perTraceMax)
    const next: Record<string, unknown> = { ...t }
    if (xValues && xValues.length > 0) {
      next.x = selectByIndices(xValues, indices)
    }
    if (yValues && yValues.length > 0) {
      next.y = selectByIndices(yValues, indices)
    }

    return next as Data
  })
}

// ============================================================================
// Preview: WebGL trace promotion (after downsampling)
// ============================================================================

function traceUsesSvgOnlyScatterFeatures(t: Record<string, unknown>): boolean {
  const fill = t.fill
  if (typeof fill === 'string' && fill.length > 0 && fill !== 'none') return true
  if (typeof t.stackgroup === 'string' && t.stackgroup.length > 0) return true
  const mode = typeof t.mode === 'string' ? t.mode : ''
  if (mode.includes('text')) return true
  const text = t.text
  if (Array.isArray(text) && text.some((x) => typeof x === 'string' && String(x).length > 0)) {
    return true
  }
  return false
}

/**
 * For static preview, promote Cartesian `scatter` → `scattergl` after pipeline downsampling
 * so many layered traces (e.g. probability grids) use Plotly’s WebGL path instead of SVG.
 * Skips traces that rely on SVG-heavy features (fills, stacking, per-point text).
 */
export function promoteScatterTracesToScatterGlForPreview(data: Data[]): Data[] {
  if (!Array.isArray(data) || data.length === 0) return data
  return data.map((trace) => {
    const t = trace as Record<string, unknown>
    const typ = typeof t.type === 'string' ? t.type.toLowerCase() : ''
    if (typ !== 'scatter') return trace
    if (traceUsesSvgOnlyScatterFeatures(t)) return trace
    return { ...trace, type: 'scattergl' } as Data
  })
}

// ============================================================================
// Report chart complexity (defer expensive decode + Plotly mount)
// ============================================================================

const BDATA_SCAN_MAX_ACCUM = 2_000_000

function accumulateBdataCharCount(value: unknown, depth: number, acc: { n: number }): void {
  if (acc.n >= BDATA_SCAN_MAX_ACCUM || depth > 10) return
  if (!value || typeof value !== 'object') return
  if (Array.isArray(value)) {
    for (const item of value) {
      accumulateBdataCharCount(item, depth + 1, acc)
      if (acc.n >= BDATA_SCAN_MAX_ACCUM) return
    }
    return
  }
  const o = value as Record<string, unknown>
  const b = o.bdata
  if (typeof b === 'string') {
    acc.n += b.length
  }
  for (const key of Object.keys(o)) {
    if (key === 'bdata') continue
    accumulateBdataCharCount(o[key], depth + 1, acc)
    if (acc.n >= BDATA_SCAN_MAX_ACCUM) return
  }
}

/** Base64 payload in traces (e.g. probability plots) — correlates with decode / layout cost. */
export const REPORT_CHART_HEAVY_BDATA_CHARS = 80_000

/** Scatter point count heuristic before pipeline downsampling. */
export const REPORT_CHART_HEAVY_SCATTER_POINTS = 6_000

/** Summed raw `y` lengths on box traces — box optimization still processes each array. */
export const REPORT_CHART_HEAVY_BOX_RAW_POINTS = 10_000

/** Scatter cap for heavy report charts once the user enables full interaction. */
export const REPORT_HEAVY_CHART_SCATTER_MAX_POINTS = 520

/** More aggressive scatter cap while the chart is in static preview mode. */
export const REPORT_HEAVY_CHART_STATIC_SCATTER_MAX_POINTS = 280

export type PlotlyChartRenderComplexity = {
  traceCount: number
  totalBdataChars: number
  estimatedScatterPoints: number
  estimatedBoxRawPointCount: number
  isHeavyReportChart: boolean
}

/**
 * Cheap structural scan: detects figures that tend to freeze the report page
 * (large Plotly `bdata`, huge scatter series, or very large raw box arrays).
 * Does not call JSON.stringify on the full figure.
 */
export function getPlotlyChartRenderComplexity(chart: PlotlyChart): PlotlyChartRenderComplexity {
  const data = Array.isArray(chart.data) ? chart.data : []
  const acc = { n: 0 }
  for (const trace of data) {
    accumulateBdataCharCount(trace, 0, acc)
  }

  let estimatedScatterPoints = 0
  let estimatedBoxRawPointCount = 0
  for (const trace of data) {
    const t = trace as Record<string, unknown>
    estimatedScatterPoints += getScatterPointCount(t)
    if (t.type === 'box' && Array.isArray(t.y)) {
      estimatedBoxRawPointCount += t.y.length
    }
  }

  const isHeavyReportChart =
    acc.n >= REPORT_CHART_HEAVY_BDATA_CHARS ||
    estimatedScatterPoints >= REPORT_CHART_HEAVY_SCATTER_POINTS ||
    estimatedBoxRawPointCount >= REPORT_CHART_HEAVY_BOX_RAW_POINTS

  return {
    traceCount: data.length,
    totalBdataChars: acc.n,
    estimatedScatterPoints,
    estimatedBoxRawPointCount,
    isHeavyReportChart,
  }
}

/** One-line explanation for the deferred-load UI; matches `isHeavyReportChart` heuristics. */
export function getHeavyReportChartHint(complexity: PlotlyChartRenderComplexity): string {
  const { totalBdataChars, estimatedBoxRawPointCount, estimatedScatterPoints } = complexity
  if (totalBdataChars >= REPORT_CHART_HEAVY_BDATA_CHARS) {
    return `Large encoded numeric series (~${Math.round(totalBdataChars / 1000)}k characters).`
  }
  if (estimatedBoxRawPointCount >= REPORT_CHART_HEAVY_BOX_RAW_POINTS) {
    return `High-volume box-plot data (${estimatedBoxRawPointCount.toLocaleString()} raw points across traces).`
  }
  return `Large scatter dataset (~${estimatedScatterPoints.toLocaleString()} points).`
}
